<template>
    <div class="sign-up-form__wrap">
        <ValidationObserver
            v-slot="{ invalid, passes }"
            tag="form"
            class="sign-up-form"
            @submit.prevent="submit"
            ref="observer">

            <div style="margin-bottom: 10px;">
              <i18n path="header_product_warning" tag="label">
                <a :href="companySignupUrl">GrabCAD Print</a>
              </i18n>
            </div>

            <FormTextInput
                :pretty-name="$t('label.first_name')"
                :placeholder="$t('placeholders.first')"
                name="first_name"
                type="text"
                v-model="member.first_name"
                :response-errors="responseErrors.first_name"
                validation-rules="required">
            </FormTextInput>

            <FormTextInput
                :pretty-name="$t('label.last_name')"
                :placeholder="$t('placeholders.last')"
                name="last_name"
                type="text"
                v-model="member.last_name"
                :response-errors="responseErrors.last_name"
                validation-rules="required">
            </FormTextInput>

            <FormTextInput
                :pretty-name="$t('label.email')"
                :placeholder="$t('placeholders.email')"
                :disabled="!allFieldsRequired"
                name="email"
                type="text"
                v-model="member.email"
                :response-errors="responseErrors.email"
                validation-rules="required|email">
            </FormTextInput>

            <FormTextInput
                :pretty-name="$t('label.password')"
                :placeholder="$t('placeholders.password')"
                name="password"
                type="password"
                v-model="member.password"
                :response-errors="responseErrors.password"
                validation-rules="required|min_password:6">
                <template slot="requirement">
                    {{$t('label.password_hint')}}
                </template>
            </FormTextInput>

            <FormMultiselectInput
                :pretty-name="$t('label.country')"
                :placeholder="$t('placeholders.country')"
                name="level"
                validation-rules="required"
                :tab-fix="true"
                :response-errors="responseErrors.country"
                :options="options.countries"
                v-model="selectedCountry">
            </FormMultiselectInput>

            <div class="fields" v-show="stateFields" v-cloak>
                <FormMultiselectInput
                        :pretty-name="$t('label.state')"
                        :placeholder="$t('placeholders.state')"
                        name="level"
                        type="text"
                        :tab-fix="true"
                        :options="options.states[member.country] || []"
                        :response-errors="responseErrors.state"
                        v-model="member.state"
                        :validation-rules="stateFields ? 'required' : ''">
                </FormMultiselectInput>
            </div>

            <template v-if="allFieldsRequired">
                <FormTextInput
                    :pretty-name="$t('label.phone_number')"
                    :placeholder="$t('placeholders.phone_number')"
                    name="phone"
                    type="text"
                    :response-errors="responseErrors.phone_number"
                    v-model="account.phone_number"
                    :validation-rules="allFieldsRequired ? 'required' : false">
                </FormTextInput>

                <FormMultiselectInput
                    :pretty-name="$t('label.industry_level')"
                    :placeholder="$t('placeholders.printer')"
                    name="level"
                    :validation-rules="allFieldsRequired ? 'required' : false"
                    label="label"
                    track-by="value"
                    :options="options.job_level"
                    :response-errors="responseErrors.level"
                    :tab-fix="true"
                    v-model="account.level">
                </FormMultiselectInput>

                <transition name="fields" mode="out-in">
                    <div class="fields" v-show="companyFields" v-cloak>
                        <FormTextInput
                            :pretty-name="$t('label.company_name')"
                            :placeholder="$t('placeholders.company_name')"
                            name="company_name"
                            type="text"
                            :response-errors="responseErrors.name"
                            v-model="account.name"
                            :validation-rules="companyFields ? 'required' : ''">
                        </FormTextInput>

                        <FormMultiselectInput
                            :pretty-name="$t('label.industry_vertical')"
                            :placeholder="$t('placeholders.printer')"
                            name="level"
                            :validation-rules="companyFields ? 'required' : ''"
                            :options="options.industry_vertical"
                            :response-errors="responseErrors.industry_vertical"
                            v-model="account.industry_vertical"
                            :tab-fix="true"
                            track-by="value"
                            label="label">
                        </FormMultiselectInput>

                        <FormMultiselectInput
                            :pretty-name="$t('label.company_size')"
                            :placeholder="$t('placeholders.printer')"
                            name="level"
                            :validation-rules="allFieldsRequired && companyFields ? 'required' : ''"
                            :options="options.company_size"
                            :response-errors="responseErrors.company_size"
                            v-model="account.company_size"
                            :tab-fix="true"
                            track-by="value"
                            label="label">
                        </FormMultiselectInput>
                    </div>
                </transition>
                <transition name="fields" mode="out-in">
                    <div class="fields" v-show="studentFields" v-cloak>
                        <FormTextInput
                            :pretty-name="$t('label.university_name')"
                            :placeholder="$t('placeholders.university_name')"
                            name="university_name"
                            type="text"
                            :response-errors="responseErrors.name"
                            v-model="account.name"
                            :validation-rules="studentFields ? 'required' : ''">
                        </FormTextInput>
                    </div>
                </transition>
            </template>

            <slot></slot>

            <div class="recaptcha-container">
                <input type="hidden" name="gc-recaptcha" v-model="gcRecaptcha">

                <div class="g-recaptcha"
                     :data-sitekey="recaptchaSiteKey"
                     data-callback="gcAfterCaptcha"
                     data-size="invisible">
                </div>
            </div>

            <p class="sign-up-form__terms">
                {{$t('terms.print_text')}} <a href="/terms" target="_blank">{{$t('terms.link')}}</a>,
                <a href="/software_terms" target="_blank">{{$t('software_terms.link')}}</a>
                {{$t('privacy_policy.and')}}
                <a href="/privacy_policy" target="_blank">{{$t('privacy_policy.link')}}</a> {{$t('privacy_policy.text')}}
            </p>

            <p v-if="responseErrors.recaptcha_failed" class="form-input__errors form-input__errors--server">
              {{ responseErrors.recaptcha_failed }}
            </p>

            <SignUpButton :current-app="currentApp" :disabled="invalid || requestInProgress">{{$t('actions.sign_up_button')}}</SignUpButton>
        </ValidationObserver>
    </div>
</template>

<script>
    import FormTextInput from '../../../shared/inputs/FormTextInput'
    import FormMultiselectInput from '../../../shared/inputs/FormMultiselectInput'
    import SignUpButton from './SignUpButton'
    import translations from '@/translations/sign_up_form/index'
    import axios from 'axios'
    import options from './form_options'

    export default {
        name: 'SignUpForm',
        props: {
            currentApp: {
                type: String
            },
            prefilledEmail: {
                type: String
            },
            confirmationToken: {
                type: String
            }
        },

        components: {
            FormTextInput,
            SignUpButton,
            FormMultiselectInput
        },

        data () {
            return {
                companySignupUrl: Grabcad.site.company_signup,
                selectedCountry: '',
                requestInProgress: false,
                responseErrors: {},
                member: {
                    email: '',
                    first_name: '',
                    last_name: '',
                    password: '',
                    country: '',
                    state: '',
                    uses_printer_for: '',
                    shop_owner: '',
                    shop_users_expected: null
                },
                account: {
                    phone_number: '',
                    level: '',
                    name: '',
                    company_size: '',
                    industry_vertical: ''
                },
                options: {
                    countries: options.countries,
                    states: Grabcad.states,
                    job_level: this.getTranslatedOptionWithLabelAndValue('job_level', 'level_values'),
                    uses_printer_for: this.getTranslatedOptionWithLabelAndValue('uses_printer_for'),
                    company_size: this.getTranslatedOptionWithLabelAndValue('company_size'),
                    industry_vertical: this.getTranslatedOptionWithLabelAndValue('industry_vertical', 'industry_values'),
                    yes_no: this.getTranslatedOptionWithLabelAndValue('yes_no', 'yes_no_values')
                },
                confirmation_token: '',
                gcRecaptcha: 'init',
                recaptchaSiteKey: Grabcad.recaptcha.siteKey || '',
                recaptchaResponse: ''
            }
        },

        watch: {
            selectedCountry(newVal, oldVal) {
                if (newVal != oldVal) {
                    this.member.country = newVal;
                    this.member.state = ''
                }
            }
        },

        computed: {
            params () {
                const {
                    gcRecaptcha,
                    confirmation_token
                } = this;

                const member = Object.assign({}, this.member);
                const account = Object.assign({}, this.account);

                member['uses_printer_for'] = member['uses_printer_for'].value;
                member['shop_owner'] = member['shop_owner'].value;
                account['company_size'] = account['company_size'].value;
                account['industry_vertical'] = account['industry_vertical'].value;
                account['level'] = account['level'].value;

                return {
                    format: 'json',
                    member,
                    account,
                    confirmation_token,
                    'gc-recaptcha': gcRecaptcha,
                    ...this.apps
                };
            },

            companyFields () {
                const { account } = this;
                return this.allFieldsRequired && account.level && account.level.value !== 'Student' && account.level.value !== 'Not employed'
            },

            studentFields () {
                const { account } = this;
                return this.allFieldsRequired && account.level && account.level.value === 'Student'
            },

            stateFields() {
                const {member} = this;

                return this.allFieldsRequired && this.options.states[member.country];
            },

            allFieldsRequired () {
                return !this.prefilledEmail || this.prefilledEmail.length === 0;
            },

            apps () {
                const apps = {};
                apps[this.currentApp] = 1;
                return apps;
            }
        },

        methods: {
            async register (recaptchaResponse = '') {
                if (recaptchaResponse.length) {
                    this.recaptchaResponse = recaptchaResponse;
                }

                try {
                    this.requestInProgress = true;
                    const { data } = await axios.post(
                        '/account',
                        {
                            ...this.params,
                            'g-recaptcha-response': recaptchaResponse
                        }
                    );
                    this.requestInProgress = false;

                    if (this.allFieldsRequired) {
                        this.$emit('success', this.params.member.email);
                    } else {
                        window.location.href = data.return_to;
                    }
                } catch (error) {
                    if (Grabcad.recaptcha.enabled) {
                        window.grecaptcha.reset();
                    }

                    this.gcRecaptcha = 'loaded';
                    this.recaptchaResponse = '';

                    this.responseErrors = error.response.data.errors;
                    this.requestInProgress = false;

                    if (this.responseErrors.recaptcha_failed) {
                        this.gcRecaptcha = 'error';
                    }
                }
            },

            submit () {
                if (Grabcad.recaptcha.enabled && !this.recaptchaResponse.length) {
                    window.grecaptcha.execute();
                } else {
                    this.register(this.recaptchaResponse);
                }
            },

            initRecaptcha () {
                if (!Grabcad.recaptcha.enabled) return;
                const recaptchaScriptUrl = "https://www.recaptcha.net/recaptcha/api.js";

                window.gcAfterCaptcha = captchaResponse => this.register(captchaResponse);

                const gcCaptchaScript = document.createElement('script');
                gcCaptchaScript.src = recaptchaScriptUrl;

                document.body.appendChild(gcCaptchaScript);

                gcCaptchaScript.onload = () => {
                    this.gcRecaptcha = 'loaded';
                };

                gcCaptchaScript.onerror = () => {
                    this.gcRecaptcha = 'error';
                };
            },

            getTranslatedOptionWithLabelAndValue (optionsKey, translationsKey) {
                const key = translationsKey ? translationsKey : optionsKey;

                return options[optionsKey].map(val => ({
                    value: this.$t(`${key}.${val}`, 'en'),
                    label: this.$t(`${key}.${val}`)
                }));
            }
        },

        mounted () {
            this.initRecaptcha();
            if (this.prefilledEmail) {
                this.member.email = this.prefilledEmail;
                this.confirmation_token = this.confirmationToken;
            }
        },

        i18n: {
            messages: translations
        }
    }
</script>

<style lang="scss" scoped>
    @import "styleguidescss/utilities/_variables.scss";

    .sign-up-form {
        box-shadow: 0px 2px 10px rgba(17, 17, 17, 0.15);
        border-radius: 5px;
        background: #FFFFFF;
        max-width: 496px;
        width: 100%;
        padding: 30px;

        &__wrap {
            padding: 20px;
            background: #F3F3F3;
            width: 100%;
            display: flex;
            flex-flow: column nowrap;
            align-items: center;
        }
    }

    .fields {
        opacity: 1;

        &-enter-active, &-leave-active {
            transition: opacity .3s;
        }

        &-enter, &-leave-to {
            opacity: 0;
        }
    }
</style>

<style lang="scss">
    @import "styleguidescss/utilities/_variables.scss";

    .sign-up-form {
        &__terms {
            font-style: normal;
            font-weight: normal;
            font-size: 13px;
            line-height: 18px;
            color: #333333;
            padding-bottom: 0;
            margin-bottom: 20px;

            a, a:visited, a:hover, a:active {
                color: $blue;
            }

            a:hover {
                text-decoration: underline;
            }
        }
    }
</style>

<style>
    .grecaptcha-logo, .grecaptcha-badge {
        display: none !important;
    }
</style>
